import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FilterBar } from "../components/FilterBar";
import { MovieCard } from "../components/MovieCard";
import { authedFetch, postJson } from "../lib/api";
import {
  applyPlanned,
  applyPlannedToMovies,
  applyPlannedToWeeks,
  planRequest,
  trackPlannerWrite,
} from "../lib/planner";
import { formatLongDate, formatShortDate, isToday, shiftDate, todayInTimeZone } from "../lib/dates";
import { detailBatches, fetchDetails } from "../lib/details";
import { fetchFlags, withFlags, type MovieFlags } from "../lib/flags";
import { type Filters, filterParams, loadFilters, saveFilters } from "../lib/filters";
import { loadPrefs, savePrefs } from "../lib/prefs";
import { RANGE_OPTIONS, type RangeId, clampDays, rangeDays } from "../lib/range";
import type {
  City,
  DoubleFeature,
  DoubleFeaturesResponse,
  Movie,
  MovieDetail,
  RecommendationsResponse,
  Showtime,
  ShowtimesResponse,
  StoredRecommendations,
} from "../lib/types";

// The Movie Finder. It answers "what is playing, when, and is it worth my
// time" -- browsing, filtering, and the two list decisions (watchlist / not
// interested). Ratings, description and cast arrive separately once the list is
// already on screen; the planner and the movie agent are separate slices. None
// of them should be able to keep the showtimes list from rendering.
//
// Several views over the same data. They are component STATE and not routes,
// deliberately: the amc-seat-extension's sync.js only writes resolved AMC links
// back to a page whose pathname is exactly `/`, so putting Search on `/search`
// would silently disable link resolution for anyone who navigated there first.
//
// Upcoming covers what used to be two views, Daily and Upcoming. Its one-day
// range still asks the single-date endpoint, so that answer keeps the
// per-theater shape and the live-scrape fallback a range endpoint refuses.

const BROWSER_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;

type View = "upcoming" | "search" | "double" | "recommend";

const VIEWS: { id: View; label: string }[] = [
  { id: "upcoming", label: "Upcoming" },
  { id: "search", label: "Search" },
  { id: "double", label: "Double features" },
  { id: "recommend", label: "Agent" },
];

/** How far ahead the recommender may look, matching `MAX_RECOMMENDER_DAYS`.
 *
 *  Shorter than Browse's 70 because every candidate becomes prompt text. The
 *  server refuses anything longer, so this only decides what the control
 *  offers.
 */
const RECOMMEND_DAY_OPTIONS = [3, 7, 14, 21];

const AGENT_STARTERS = [
  "What should I see this week?",
  "Plan two weeknight movies after 6 PM.",
  "Are there any unusual or one-night-only screenings?",
];

/** Score colours, against Gemini's own per-batch calibration.
 *
 *  The server sends the band rather than the colour: which numbers count as
 *  good is a property of the batch the model scored, not of this stylesheet.
 */
const SCORE_BAND_CLASS: Record<string, string> = {
  high: "text-emerald-400",
  medium: "text-amber-400",
  low: "text-neutral-500",
};

/** Search's range options, in weeks. Upcoming has its own, in `lib/range`. */
const WEEK_OPTIONS = [1, 2, 4, 10];

/** How long to sit on a keystroke before searching.
 *
 *  A search spans up to 70 dates server-side, so firing per keystroke would
 *  queue a range fetch for every prefix of the word being typed. 350ms is long
 *  enough to swallow a burst of typing and short enough not to feel like a
 *  submit button.
 */
const SEARCH_DEBOUNCE_MS = 350;

function DoubleFeatureCard({ pair }: { pair: DoubleFeature }) {
  const line = (movie: DoubleFeature["movie1"]) => (
    <div className="flex flex-wrap items-baseline gap-x-2 text-sm">
      <span className="font-medium text-neutral-100">{movie.title}</span>
      <span className="text-neutral-400">
        {movie.start} – {movie.end}
      </span>
      {movie.format && (
        <span className="text-[10px] uppercase tracking-wide text-neutral-500">
          {movie.format}
        </span>
      )}
      {movie.ticket_url && (
        <a
          href={movie.ticket_url}
          target="_blank"
          rel="noreferrer"
          className="text-neutral-400 underline hover:text-neutral-200"
        >
          🎟️
        </a>
      )}
    </div>
  );

  return (
    <article
      className={`space-y-1 rounded-lg border border-neutral-800 bg-neutral-900/50 p-4 ${
        // Dimmed rather than dropped, exactly as a past showtime is: on a past
        // date every pair is started, and hiding them all would read as "there
        // were none" instead of "they have been and gone".
        pair.is_past ? "opacity-50" : ""
      }`}
    >
      <p className="text-xs text-neutral-500">{pair.theater_name}</p>
      {line(pair.movie1)}
      <p className="text-xs italic text-neutral-500">
        {pair.gap_minutes > 0 ? `↓ ${pair.gap_minutes} min break` : "↓ Back-to-back"}
      </p>
      {line(pair.movie2)}
      <p className="text-xs text-neutral-500">Total: {pair.total_display}</p>
    </article>
  );
}

export function Showtimes() {
  const [cities, setCities] = useState<City[] | null>(null);
  const [cityId, setCityId] = useState<string | null>(() => loadPrefs().cityId);
  const [theaterIds, setTheaterIds] = useState<string[]>(() => loadPrefs().theaterIds);
  const [filters, setFilters] = useState<Filters>(loadFilters);
  const [premiumFormatOptions, setPremiumFormatOptions] = useState<string[]>([]);
  const [date, setDate] = useState<string | null>(null);
  // `?q=` is how the Planner links a film back here for its full listings.
  // Read ONCE into initial state rather than watched as a route: the view is
  // component state precisely because the Chrome extension returns early
  // unless `pathname === '/'`, and a query string keeps that true. Reading it
  // lazily also means the first fetch is already the search, with no
  // daily-view request fired and thrown away.
  const initialQuery = useMemo(
    () => new URLSearchParams(window.location.search).get("q")?.trim() ?? "",
    [],
  );
  const [view, setView] = useState<View>(initialQuery ? "search" : "upcoming");
  const [range, setRange] = useState<RangeId>("day");
  const [weeks, setWeeks] = useState<number>(2);
  const [maxGap, setMaxGap] = useState<number>(30);
  const [queryInput, setQueryInput] = useState(initialQuery);
  const [query, setQuery] = useState(initialQuery);
  const [data, setData] = useState<ShowtimesResponse | null>(null);
  const [doubles, setDoubles] = useState<DoubleFeaturesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [writeError, setWriteError] = useState<string | null>(null);
  // A write the server ACCEPTED but could not confirm. Separate from
  // `writeError` because the two want opposite renderings of the star: an
  // error puts it back the way it was, this one leaves it where you clicked.
  const [writeWarning, setWriteWarning] = useState<string | null>(null);
  // Keyed by title rather than held on the movie, because a film's ratings do
  // not depend on the date being viewed -- see the fetch effect below.
  const [details, setDetails] = useState<Record<string, MovieDetail>>({});
  // The deferred closing / Sarah badges, keyed by title and NOT cleared
  // between dates for the same reason `details` is not: neither answer is
  // about a screening, so walking the day strip keeps them rather than
  // flashing badge-less cards while the identical lookups run again.
  const [flags, setFlags] = useState<Record<string, MovieFlags>>({});
  const [recs, setRecs] = useState<RecommendationsResponse | null>(null);
  const [recDays, setRecDays] = useState(7);
  const [recBusy, setRecBusy] = useState(false);
  const [recError, setRecError] = useState<string | null>(null);
  const [preference, setPreference] = useState("");
  // Prevent the free stored-conversation read from overwriting a newer paid
  // turn when the user sends quickly after opening the tab.
  const recActionVersion = useRef(0);
  const suppressNextRecRead = useRef(false);

  const city = useMemo(
    () => cities?.find((c) => c.id === cityId) ?? cities?.find((c) => c.is_default) ?? null,
    [cities, cityId],
  );
  // Before the city list arrives, fall back to the browser's zone. Only used to
  // pick an initial date, and it is replaced the moment the real zone is known.
  const timezone = city?.timezone ?? data?.city.timezone ?? BROWSER_TZ;
  // Both ranges are clamped against the THEATER's today, which is the day the
  // server measures its date window from.
  const today = todayInTimeZone(timezone);
  const upcomingDays = date ? rangeDays(range, date, today) : 1;
  const searchDays = date ? clampDays(weeks * 7, date, today) : weeks * 7;

  useEffect(() => {
    let active = true;
    authedFetch("/api/cities")
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((body: { cities: City[] }) => {
        if (active) setCities(body.cities);
      })
      .catch(() => {
        // Non-fatal. Without the city list the picker is unavailable, but the
        // showtimes request still works against the server's default city.
        if (active) setCities([]);
      });
    return () => {
      active = false;
    };
  }, []);

  // Premium format labels come from the server so the client cannot keep
  // offering one that PREMIUM_FORMATS has since dropped.
  useEffect(() => {
    let active = true;
    authedFetch("/api/filter-options")
      .then((res) => (res.ok ? res.json() : { premium_formats: [] }))
      .then((body: { premium_formats: string[] }) => {
        if (active) setPremiumFormatOptions(body.premium_formats ?? []);
      })
      .catch(() => {
        // The premium checkbox still works; it just means "any premium format".
      });
    return () => {
      active = false;
    };
  }, []);

  // Today is resolved once the theater's timezone is known, not the browser's.
  useEffect(() => {
    setDate((current) => current ?? todayInTimeZone(timezone));
  }, [timezone]);

  // Typing settles before a range fetch goes out. `queryInput` drives the box
  // so it stays responsive; `query` is what the request reads.
  useEffect(() => {
    const timer = setTimeout(() => setQuery(queryInput), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [queryInput]);

  // ---- The recommender -------------------------------------------------
  //
  // Its own state rather than a fifth branch of `request`, because it is the
  // one view that must not fetch on its own. A recommendation is a paid Gemini
  // call; the GET below is the free half (whatever was last generated for this
  // range) and is what the page shows on arrival.
  const recParams = useMemo(() => {
    if (!date) return null;
    const params = new URLSearchParams({ start: date, days: String(recDays) });
    if (cityId) params.set("city", cityId);
    for (const id of theaterIds) params.append("theaters", id);
    return params;
  }, [date, recDays, cityId, theaterIds]);

  const recBody = useMemo(
    () => ({
      start: date,
      days: recDays,
      ...(cityId ? { city: cityId } : {}),
      ...(theaterIds.length > 0 ? { theaters: theaterIds } : {}),
    }),
    [date, recDays, cityId, theaterIds],
  );

  useEffect(() => {
    if (view !== "recommend" || !recParams) return;
    if (suppressNextRecRead.current) {
      suppressNextRecRead.current = false;
      return;
    }
    let cancelled = false;
    const actionVersion = recActionVersion.current;
    setRecError(null);
    setRecs(null);
    void (async () => {
      try {
        const res = await authedFetch(`/api/recommendations?${recParams}`);
        if (cancelled || !res.ok) return;
        const body = (await res.json()) as StoredRecommendations;
        if (!cancelled && actionVersion === recActionVersion.current) {
          setRecs(body.recommendations_body);
        }
      } catch {
        // Silent: this is the "what did you say last time" read, and failing
        // it costs nothing the user asked for. The generate button reports
        // its own failures loudly.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [view, recParams]);

  const askRecommender = useCallback(
    async (path: string, body: unknown) => {
      recActionVersion.current += 1;
      setRecBusy(true);
      setRecError(null);
      try {
        setRecs(await postJson<RecommendationsResponse>(path, body));
        setPreference("");
      } catch (err) {
        setRecError(err instanceof Error ? err.message : String(err));
      } finally {
        setRecBusy(false);
      }
    },
    [],
  );

  const sendAgentMessage = useCallback(
    (message: string) => {
      const content = message.trim();
      if (!content || recBusy || !date) return;
      // A natural-language "next two weeks" request should not silently run
      // against the default seven-day window. Expand a new conversation before
      // sending it; the range picker remains the explicit control thereafter.
      const asksForTwoWeeks = /\b(?:next\s+)?(?:two|2)\s+weeks?\b/i.test(content);
      const requestedDays = !recs && asksForTwoWeeks ? 14 : recDays;
      if (requestedDays !== recDays) {
        suppressNextRecRead.current = true;
        setRecDays(requestedDays);
      }
      void askRecommender(
        recs ? "/api/recommendations/refine" : "/api/recommendations",
        recs
          ? { ...recBody, feedback: content }
          : { ...recBody, days: requestedDays, preference: content },
      );
    },
    [askRecommender, date, recBody, recBusy, recDays, recs],
  );

  /** Path and params for whichever view is showing. */
  const request = useMemo(() => {
    if (!date) return null;
    const params = filterParams(filters);
    if (cityId) params.set("city", cityId);
    for (const id of theaterIds) params.append("theaters", id);

    // `pairs` travels with the request rather than being re-derived from the
    // path further down. The path and the response shape are decided by the
    // same `view` here, and re-deriving one from the other by string-matching
    // is a second copy of this routing table that has to be kept in step by
    // hand.
    // The recommender is deliberately outside this memo. Everything here
    // re-fetches whenever any of its inputs change, and a recommendation costs
    // a paid Gemini call -- so it is an explicit action with its own state
    // below, not something a filter checkbox can trigger.
    if (view === "recommend") return null;
    // One day goes to the single-date endpoint, not to a one-day range. That
    // one can live-scrape a date the nightly export missed, and it answers in
    // the per-theater shape, with no date heading repeated on every film.
    if (view === "upcoming" && upcomingDays === 1) {
      params.set("date", date);
      return { path: "/api/showtimes", params, pairs: false };
    }
    if (view === "double") {
      params.set("date", date);
      params.set("max_gap_minutes", String(maxGap));
      return { path: "/api/double-features", params, pairs: true };
    }
    params.set("start", date);
    if (view === "search") {
      params.set("days", String(searchDays));
      params.set("q", query);
      return { path: "/api/search", params, pairs: false };
    }
    params.set("days", String(upcomingDays));
    return { path: "/api/showtimes/upcoming", params, pairs: false };
  }, [date, view, upcomingDays, searchDays, maxGap, query, cityId, theaterIds, filters]);

  const load = useCallback(
    async (signal: { cancelled: boolean }) => {
      if (!request) return;
      setLoading(true);
      setError(null);
      // Drop the OTHER shape's results now, before the await, not when the
      // response lands. Movie cards and double-feature cards are different
      // components under a heading that has already switched, so leaving the
      // wrong one up for the length of the fetch renders pair cards under
      // "Upcoming" (or a movie list under "Double features"). Same-shape switches
      // deliberately keep their stale list dimmed while it refreshes, exactly
      // as walking the day strip does.
      if (request.pairs) setData(null);
      else {
        setDoubles(null);
        // Search is intentionally empty until the user enters a query. Clear
        // the previous tab's movie list immediately, rather than leaving it
        // visible while the empty search request is in flight.
        if (view === "search" && !query) setData(null);
      }

      try {
        const res = await authedFetch(`${request.path}?${request.params}`);
        if (signal.cancelled) return;
        if (!res.ok) {
          // 502 is the API telling us the upstream scrape failed, which is a
          // different problem from a bug here and worth saying so.
          throw new Error(
            res.status === 502
              ? "Could not reach AMC or Fandango. They may be blocking requests right now."
              : `Request failed (HTTP ${res.status})`,
          );
        }
        const body = await res.json();
        if (signal.cancelled) return;
        // Only ONE of the two is ever populated. The other was already cleared
        // at the top of this function; clearing it again here would be the
        // no-op that made the earlier version look correct.
        if (request.pairs) setDoubles(body as DoubleFeaturesResponse);
        else if (view === "search" && !query) setData(null);
        else setData(body as ShowtimesResponse);
      } catch (err) {
        if (!signal.cancelled) setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!signal.cancelled) setLoading(false);
      }
    },
    [request, view, query],
  );

  useEffect(() => {
    const signal = { cancelled: false };
    void load(signal);
    return () => {
      signal.cancelled = true;
    };
  }, [load]);

  // Ratings, description and cast, fetched only once the showtimes are on
  // screen -- the whole reason `/api/movies/details` is a second endpoint.
  //
  // Keyed on the TITLE LIST, not on `data`. `data` is a fresh object after
  // every showtimes fetch AND after every optimistic list toggle, so depending
  // on it would restart the run each time you clicked a star mid-load. Two
  // different filter settings also routinely return the same films, and
  // re-requesting their details would be pure cost.
  //
  // `details` is keyed by title and deliberately NOT cleared between dates: a
  // film has the same ratings on Tuesday as on Monday, so walking the day strip
  // renders them at once rather than flashing rating-less cards while the
  // identical lookups run again.
  //
  // Sourced from whichever view is showing. The recommender returns the SAME
  // movie payloads, so its cards want the same ratings, synopsis and trailer;
  // reading only `data` left every recommended card permanently detail-less.
  const shownMovies = useMemo(
    () =>
      view === "recommend"
        ? (recs?.recommendations ?? [])
            .map((rec) => rec.movie)
            .filter((movie): movie is Movie => movie !== null)
        : (data?.movies ?? []),
    [view, recs, data],
  );
  const shownDate =
    view === "recommend" ? (recs?.start ?? "") : (data?.date ?? data?.start ?? "");

  const detailKey = shownDate
    ? [shownDate, ...shownMovies.map((m) => m.title)].join("|")
    : "";

  // Holds what the request needs without putting it in the dependency list.
  // Declared BEFORE the effect below so it has already run for this commit by
  // the time that one fires -- effects in a commit run in declaration order.
  const detailRequest = useRef({
    date: "",
    cityId,
    theaterIds,
    movies: [] as Movie[],
  });
  useEffect(() => {
    detailRequest.current = {
      date: shownDate,
      cityId,
      theaterIds,
      movies: shownMovies,
    };
  });

  useEffect(() => {
    if (!detailKey) return;
    let cancelled = false;
    const { date: fallback, cityId: city, theaterIds: theaters, movies } = detailRequest.current;

    // Sequential across batches for the same reason `fetchDetails` is
    // sequential across chunks: every one of these fans out to TMDb, OMDb and
    // RT, and a range view can produce a dozen of them at once.
    void (async () => {
      for (const batch of detailBatches(movies, fallback)) {
        if (cancelled) return;
        await fetchDetails(
          { date: batch.date, cityId: city, theaterIds: theaters, titles: batch.titles },
          (chunk) =>
            setDetails((current) => {
              const next = { ...current };
              for (const detail of chunk) next[detail.title] = detail;
              return next;
            }),
          () => cancelled,
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [detailKey]);

  // The second pass for the two badges `/api/showtimes` hands over. Keyed on
  // the same title list as the details run above, plus the city: unlike
  // details, a closing score is per city (it aggregates that city's theaters)
  // so the same film in Boston is a different answer.
  useEffect(() => {
    if (!detailKey) return;
    let cancelled = false;
    const titles = detailRequest.current.movies.map((movie) => movie.title);
    void fetchFlags(
      { cityId, titles },
      (chunk) =>
        setFlags((current) => {
          const next = { ...current };
          for (const entry of chunk) next[entry.title] = entry;
          return next;
        }),
      () => cancelled,
    );
    return () => {
      cancelled = true;
    };
  }, [detailKey, cityId]);

  const chooseCity = (id: string) => {
    setCityId(id);
    // Theaters are per-city ids, so keeping the old ones would silently select
    // nothing in the new city and fall back to its defaults anyway.
    setTheaterIds([]);
    savePrefs({ cityId: id, theaterIds: [] });
    setData(null);
    setDoubles(null);
  };

  const toggleTheater = (id: string) => {
    const next = theaterIds.includes(id)
      ? theaterIds.filter((t) => t !== id)
      : [...theaterIds, id];
    setTheaterIds(next);
    savePrefs({ cityId, theaterIds: next });
  };

  const changeFilters = (next: Filters) => {
    setFilters(next);
    saveFilters(next);
  };

  /**
   * Write one list membership and reflect it on the card.
   *
   * Optimistic, then reconciled against the server's answer -- which is the
   * authority because it applied the same normalization the filters use. On
   * failure the card is put back the way it was and the reason is surfaced; a
   * star that stays lit after a failed write is worse than no star.
   *
   * The list is NOT re-fetched. Re-running the query would make the row you
   * just marked "not interested" disappear mid-click under `hideNotInterested`,
   * taking the undo with it.
   */
  const setList = useCallback(
    async (title: string, list: "watchlist" | "not-interested", on: boolean) => {
      const key = list === "watchlist" ? "in_watchlist" : "not_interested";
      const apply = (value: boolean) =>
        setData((current) =>
          current
            ? {
                ...current,
                movies: current.movies.map((m) =>
                  m.title === title ? { ...m, [key]: value } : m,
                ),
              }
            : current,
        );

      setWriteError(null);
      setWriteWarning(null);
      apply(on);
      try {
        const body = await postJson<{ warning?: string } & Record<string, unknown>>(
          `/api/${list}`,
          { title, on },
        );
        apply(Boolean(body[key]));
        // The watchlist write goes to throw-rag inline, and throw-rag can
        // accept one without confirming Letterboxd took it. That is neither a
        // success to render silently nor a failure to undo: the star stays
        // where it was clicked (the underlying write is idempotent, so
        // clicking again is safe) and says so. Nothing drains a pending
        // mutation, so the copy must not promise it resolves itself.
        if (typeof body.warning === "string" && body.warning) {
          setWriteWarning(`${title}: ${body.warning}`);
        }
      } catch (err) {
        apply(!on);
        setWriteError(err instanceof Error ? err.message : String(err));
      }
    },
    [],
  );

  /**
   * Plan or unplan one screening, optimistically.
   *
   * Optimistic for the same reason `setList` is: the round trip includes a
   * Fandango price lookup and a Google Sheets write, which is seconds, and a
   * chip that does nothing for seconds reads as broken. On failure the chip
   * flips back and the error surfaces, rather than the list re-fetching -- a
   * re-fetch would also re-run the query, and the "week full" state that
   * caused the failure is exactly when you want the rest of the page to stay
   * put while you decide what to drop.
   *
   * `applyPlanned` moves the week counter too, so the OTHER chips in the same
   * week stop offering a slot the moment the fourth one is taken.
   */
  const setPlanned = useCallback(
    async (theater: string, showtime: Showtime, planned: boolean) => {
      // Applied to BOTH result sets, not to whichever view is showing. They
      // hold the same films, and switching to Recommend right after planning
      // from Upcoming must not show the chip unplanned again.
      const apply = (value: boolean) => {
        setData((current) =>
          current ? applyPlanned(current, theater, showtime, value) : current,
        );
        setRecs((current) =>
          current
            ? {
                ...current,
                recommendations: current.recommendations.map((rec) =>
                  rec.movie
                    ? {
                        ...rec,
                        movie: applyPlannedToMovies(
                          [rec.movie], theater, showtime, value)[0],
                      }
                    : rec,
                ),
                itinerary: (current.itinerary ?? []).map((item) =>
                  item.theater === theater &&
                  item.showtime.date === showtime.date &&
                  item.showtime.time === showtime.time &&
                  item.showtime.movie_title === showtime.movie_title
                    ? { ...item, showtime: { ...item.showtime, planned: value } }
                    : item,
                ),
                weeks: applyPlannedToWeeks(
                  current.weeks, showtime.week_key, value ? 1 : -1),
              }
            : current,
        );
      };

      setWriteError(null);
      apply(planned);
      try {
        await trackPlannerWrite(postJson(
          planned ? "/api/planner/plan" : "/api/planner/unplan",
          planRequest(theater, showtime, { cityId, theaterIds }),
        ));
        // Start the "stay for credits" lookup now, so the planner already
        // has it. Fire-and-forget: the planner page asks again if this fails.
        if (planned) {
          void Promise.resolve()
            .then(() => postJson("/api/planner/credits", { city: cityId }))
            .catch(() => undefined);
        }
      } catch (err) {
        apply(!planned);
        setWriteError(err instanceof Error ? err.message : String(err));
      }
    },
    // The location is in the deps because the plan body carries it. An empty
    // list here is what hid the 404: the callback was built once, before a
    // city was ever picked, so it could not have sent one.
    [cityId, theaterIds],
  );

  const days = useMemo(
    () => (date ? [-1, 0, 1, 2, 3, 4, 5].map((n) => shiftDate(date, n)) : []),
    [date],
  );

  const hiddenBy = data?.filter_stats?.hidden_by ?? [];
  const unavailable = data?.unavailable_dates ?? [];
  const isRange = (view === "upcoming" && upcomingDays > 1) || view === "search";
  const heading =
    view === "recommend"
      ? "Movie agent"
      : view === "search"
      ? "Search"
      : view === "upcoming" && date && upcomingDays > 1
        // Both ends named rather than "N days from": the clamp and the
        // Thursday cut-off both make N something you would have to work out.
        ? `${formatShortDate(date)} – ${formatShortDate(shiftDate(date, upcomingDays - 1))}`
        : date
          ? formatLongDate(date)
          : "Showtimes";

  return (
    <div className="mx-auto max-w-3xl space-y-5 p-4 pb-16">
      <header className="space-y-3">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h1 className="text-xl font-semibold">{heading}</h1>
          {view !== "recommend" && data && (
            <p className="text-xs text-neutral-500">
              {data.movie_count} movies · {data.showtime_count} showtimes ·{" "}
              {data.theaters.length} theaters
            </p>
          )}
          {view === "recommend" && recs && (
            <p className="text-xs text-neutral-500">
              {recs.recommendations.length} of {recs.candidate_count} ranked
            </p>
          )}
          {doubles && (
            <p className="text-xs text-neutral-500">
              {doubles.pair_count} pair{doubles.pair_count === 1 ? "" : "s"}
            </p>
          )}
        </div>

        <div className="flex flex-wrap gap-1.5">
          {VIEWS.map((option) => (
            <button
              key={option.id}
              onClick={() => setView(option.id)}
              aria-pressed={view === option.id}
              className={`rounded-md px-3 py-1 text-sm ${
                view === option.id
                  ? "bg-neutral-100 text-neutral-900"
                  : "border border-neutral-800 text-neutral-300 hover:border-neutral-600"
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>

        {view === "search" && (
          <input
            type="search"
            value={queryInput}
            onChange={(e) => setQueryInput(e.target.value)}
            placeholder="Movie title, e.g. Sinners"
            aria-label="Movie title"
            className="w-full rounded-md border border-neutral-800 bg-neutral-900 px-3 py-2 text-sm"
          />
        )}

        {/* Day strip. Yesterday is included because a late-evening check for
            "what did I miss" is a real use, and the API marks those past.
            A range view reads this as its START date, so the strip stays. */}
        <div className="flex gap-1.5 overflow-x-auto pb-1">
          {days.map((day) => (
            <button
              key={day}
              onClick={() => setDate(day)}
              className={`shrink-0 rounded-md px-3 py-1.5 text-sm ${
                day === date
                  ? "bg-neutral-100 text-neutral-900"
                  : "border border-neutral-800 text-neutral-300 hover:border-neutral-600"
              }`}
            >
              {isToday(day, timezone) ? "Today" : formatShortDate(day)}
            </button>
          ))}
        </div>

        {view === "upcoming" && (
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="text-neutral-500">Range</span>
            {RANGE_OPTIONS.map((option) => (
              <button
                key={option.id}
                onClick={() => setRange(option.id)}
                aria-pressed={range === option.id}
                className={`rounded-full px-2.5 py-1 text-xs ${
                  range === option.id
                    ? "bg-neutral-700 text-neutral-100"
                    : "border border-neutral-800 text-neutral-500 hover:border-neutral-600"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        )}

        {view === "search" && (
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="text-neutral-500">Range</span>
            {WEEK_OPTIONS.map((option) => (
              <button
                key={option}
                onClick={() => setWeeks(option)}
                aria-pressed={weeks === option}
                className={`rounded-full px-2.5 py-1 text-xs ${
                  weeks === option
                    ? "bg-neutral-700 text-neutral-100"
                    : "border border-neutral-800 text-neutral-500 hover:border-neutral-600"
                }`}
              >
                {option} week{option === 1 ? "" : "s"}
              </button>
            ))}
          </div>
        )}

        {view === "recommend" && (
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="text-neutral-500">Looking ahead</span>
              {RECOMMEND_DAY_OPTIONS.map((option) => (
                <button
                  key={option}
                  onClick={() => setRecDays(option)}
                  aria-pressed={recDays === option}
                  className={`rounded-full px-2.5 py-1 text-xs ${
                    recDays === option
                      ? "bg-neutral-700 text-neutral-100"
                      : "border border-neutral-800 text-neutral-500 hover:border-neutral-600"
                  }`}
                >
                  {option} days
                </button>
              ))}
            </div>
            {!recs && (
              <div className="flex flex-wrap gap-1.5">
                {AGENT_STARTERS.map((starter) => (
                  <button
                    key={starter}
                    onClick={() => sendAgentMessage(starter)}
                    disabled={recBusy || !date}
                    className="rounded-full border border-neutral-800 px-2.5 py-1 text-xs text-neutral-400 hover:border-neutral-600 hover:text-neutral-200 disabled:opacity-40"
                  >
                    {starter}
                  </button>
                ))}
              </div>
            )}
            <form
              className="flex flex-wrap gap-2"
              onSubmit={(event) => {
                event.preventDefault();
                sendAgentMessage(preference);
              }}
            >
              <input
                value={preference}
                onChange={(e) => setPreference(e.target.value)}
                placeholder="Ask for a movie, comparison, or weekly plan…"
                aria-label="Message the movie agent"
                maxLength={1000}
                className="min-w-[12rem] flex-1 rounded-md border border-neutral-800 bg-neutral-900 px-3 py-2 text-sm"
              />
              <button
                type="submit"
                disabled={recBusy || !date || !preference.trim()}
                className="rounded-md bg-neutral-100 px-3 py-2 text-sm text-neutral-900 disabled:opacity-40"
              >
                Send
              </button>
              {recs && (
                <button
                  type="button"
                  onClick={() => {
                    setRecs(null);
                    setPreference("");
                  }}
                  disabled={recBusy}
                  className="rounded-md border border-neutral-700 px-3 py-2 text-sm disabled:opacity-40"
                >
                  New conversation
                </button>
              )}
            </form>
          </div>
        )}

        {view === "double" && (
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="text-neutral-500">Max gap</span>
            {[30, 60].map((option) => (
              <button
                key={option}
                onClick={() => setMaxGap(option)}
                aria-pressed={maxGap === option}
                className={`rounded-full px-2.5 py-1 text-xs ${
                  maxGap === option
                    ? "bg-neutral-700 text-neutral-100"
                    : "border border-neutral-800 text-neutral-500 hover:border-neutral-600"
                }`}
              >
                {option} min
              </button>
            ))}
          </div>
        )}

        {cities && cities.length > 1 && (
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={city?.id ?? ""}
              onChange={(e) => chooseCity(e.target.value)}
              className="rounded-md border border-neutral-800 bg-neutral-900 px-2 py-1 text-sm"
            >
              {cities.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
            <div className="flex flex-wrap gap-1.5">
              {city?.theaters.map((theater) => {
                const on = theaterIds.length === 0
                  ? city.default_theaters.includes(theater.id)
                  : theaterIds.includes(theater.id);
                return (
                  <button
                    key={theater.id}
                    onClick={() => toggleTheater(theater.id)}
                    className={`rounded-full px-2.5 py-1 text-xs ${
                      on
                        ? "bg-neutral-700 text-neutral-100"
                        : "border border-neutral-800 text-neutral-500 hover:border-neutral-600"
                    }`}
                  >
                    {theater.name.replace(/^AMC /, "")}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <FilterBar
          filters={filters}
          premiumFormatOptions={premiumFormatOptions}
          onChange={changeFilters}
          // Double features carry no badges and no Sarah answers, so both
          // whole-film controls would be checkboxes that change nothing. The
          // saved preferences survive; only the controls are withheld.
          showFilmFilters={view !== "double"}
        />

        {/* Counts are per-filter and independent, so they do not sum to the
            difference. Saying "hidden" rather than showing arithmetic keeps
            that honest, and turns a suspiciously short list into something
            self-explaining rather than something that looks like an outage. */}
        {hiddenBy.length > 0 && (
          <p className="text-xs text-neutral-500">
            Hidden: {hiddenBy.map((h) => `${h.count} ${h.label}`).join(" · ")}
          </p>
        )}

        {/* Said out loud rather than left as a gap in the results. A range
            endpoint never live-scrapes, so a date the nightly export missed is
            simply absent -- and a silently short range looks like a quiet week
            rather than like missing data. */}
        {unavailable.length > 0 && (
          <p className="text-xs text-amber-500/80">
            No data for {unavailable.length} date{unavailable.length === 1 ? "" : "s"} in this
            range ({unavailable.join(", ")}).
          </p>
        )}

        {/* An unreachable sarah-site leaves her filter working off the last
            answers this server saw, or off none at all -- and an empty list
            would otherwise read as "she wants to see nothing". */}
        {filters.sarahOnly && data && !data.sarah_prefs_available && (
          <p className="text-xs text-amber-500/80">
            Could not reach Sarah&apos;s answers just now, so this list may be out of date or
            empty.
          </p>
        )}

        {/* Hide Seen fails open, so an unreadable seen set shows every film
            as unseen -- and silence would read as "you have not seen these".
            `=== false` because the double-features payload has no such key. */}
        {filters.hideSeen && data?.seen_available === false && (
          <p className="text-xs text-amber-500/80">
            Could not read your seen films just now, so films you have already seen may be
            showing.
          </p>
        )}
      </header>

      {error && (
        <div role="alert" className="rounded-md border border-red-900 bg-red-950/40 p-4 text-sm">
          {error}
        </div>
      )}

      {writeError && (
        <div role="alert" className="rounded-md border border-amber-900 bg-amber-950/40 p-3 text-sm">
          {writeError}
        </div>
      )}

      {writeWarning && (
        <div role="status" className="rounded-md border border-amber-900 bg-amber-950/40 p-3 text-sm">
          {writeWarning}
        </div>
      )}

      {view !== "recommend" && loading && !data && !doubles && (
        <p className="text-sm text-neutral-500">Loading showtimes…</p>
      )}

      {view === "search" && !query && !loading && (
        <p className="text-sm text-neutral-500">
          Type a title to find it across the next {searchDays} days.
        </p>
      )}

      {view !== "recommend" &&
        data &&
        data.movies.length === 0 &&
        !loading &&
        !(view === "search" && !query) && (
        <p className="text-sm text-neutral-500">
          {view === "search"
            ? `Nothing matching “${query}” at the selected theaters in this range.`
            : hiddenBy.length > 0
              ? `Every movie ${isRange ? "in this range" : "on this date"} is hidden by a filter.`
              : `Nothing playing at the selected theaters ${
                  isRange ? "in this range" : "on this date"
                }.`}
        </p>
      )}

      {doubles && doubles.pairs.length === 0 && !loading && (
        <p className="text-sm text-neutral-500">
          No back-to-back pairs on this date. A wider gap or fewer filters may find some.
        </p>
      )}

      {view === "recommend" && (
        <>
          {recError && (
            <div role="alert" className="rounded-md border border-red-900 bg-red-950/40 p-4 text-sm">
              {recError}
            </div>
          )}
          {recBusy && (
            <p className="text-sm text-neutral-500">
              Reading what is playing and asking Gemini… this takes a few seconds.
            </p>
          )}
          {!recBusy && !recs && !recError && (
            <p className="text-sm text-neutral-500">
              Ask for a recommendation, compare what is playing, or build a schedule.
            </p>
          )}
          <div className="space-y-2">
            {(recs?.messages ?? []).map((message, index) => (
              <p
                key={`${message.role}-${index}`}
                className={`max-w-[90%] rounded-lg p-3 text-sm whitespace-pre-wrap ${
                  message.role === "user"
                    ? "ml-auto bg-neutral-100 text-neutral-900"
                    : "border border-neutral-800 bg-neutral-900/50 text-neutral-300"
                }`}
              >
                {message.content}
              </p>
            ))}
          </div>
          {recs && (recs.messages ?? []).length === 0 && recs.summary && (
            <p className="rounded-md border border-neutral-800 bg-neutral-900/50 p-3 text-sm text-neutral-300">
              {recs.summary}
            </p>
          )}
          {recs && (recs.itinerary ?? []).length > 0 && (
            <section className="space-y-2 rounded-lg border border-neutral-800 bg-neutral-900/30 p-3">
              <h2 className="text-sm font-medium text-neutral-200">Suggested plan</h2>
              {(recs.itinerary ?? []).map((item) => (
                <div
                  key={`${item.title}-${item.date}-${item.theater}-${item.showtime.time}`}
                  className="flex flex-wrap items-center gap-x-2 gap-y-1 border-t border-neutral-800 pt-2 text-sm first:border-0 first:pt-0"
                >
                  <span className="font-medium text-neutral-100">{item.title}</span>
                  <span className="text-neutral-400">
                    {formatLongDate(item.date)} · {item.showtime.display_time} · {item.theater}
                  </span>
                  {item.showtime.format && (
                    <span className="text-[10px] uppercase tracking-wide text-neutral-500">
                      {item.showtime.format}
                    </span>
                  )}
                  <span className="basis-full text-xs text-neutral-500">{item.reason}</span>
                  <button
                    onClick={() =>
                      void setPlanned(item.theater, item.showtime, !item.showtime.planned)
                    }
                    aria-label={`${item.showtime.planned ? "Remove" : "Add"} ${item.title} ${
                      item.showtime.planned ? "from" : "to"
                    } planner`}
                    className="text-xs underline text-neutral-400 hover:text-neutral-200"
                  >
                    {item.showtime.planned ? "Remove from planner" : "Add to planner"}
                  </button>
                </div>
              ))}
            </section>
          )}
          <div className={`space-y-3 ${recBusy ? "opacity-50" : ""}`}>
            {recs?.recommendations.map((rec) => (
              <div key={`${rec.rank}-${rec.title}`} className="space-y-1">
                <div className="flex flex-wrap items-baseline gap-2 text-sm">
                  <span className="text-neutral-500">#{rec.rank}</span>
                  <span className={`text-xs ${SCORE_BAND_CLASS[rec.score_band]}`}>
                    {rec.score ?? "–"}
                  </span>
                  <span className="text-neutral-300">{rec.reason}</span>
                </div>
                {/* A recommendation the server could not match back to a film
                    keeps its place in the list rather than being dropped, so
                    the ranking the model wrote still numbers the way its
                    summary describes. */}
                {rec.movie ? (
                  <MovieCard
                    movie={withFlags(rec.movie, flags[rec.movie.title])}
                    detail={details[rec.movie.title]}
                    weeks={recs.weeks}
                    onSetList={setList}
                    onPlan={setPlanned}
                  />
                ) : (
                  <p className="rounded-lg border border-neutral-800 bg-neutral-900/50 p-4 text-sm text-neutral-500">
                    {rec.title} — no longer in the listings for this range.
                  </p>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {/* The browsing results, and only when a browsing view is showing.
          `request` returns null for the recommender, so `data` still holds
          whatever Upcoming last fetched -- ungated, those cards render underneath
          the recommendations, duplicating every film and every plan control. */}
      <div className={`space-y-3 ${loading ? "opacity-50" : ""}`}>
        {view !== "recommend" && data?.movies.map((movie) => (
          <MovieCard
            key={movie.title}
            movie={withFlags(movie, flags[movie.title])}
            detail={details[movie.title]}
            weeks={data?.weeks}
            onSetList={setList}
            onPlan={setPlanned}
          />
        ))}
        {doubles?.pairs.map((pair, index) => (
          <DoubleFeatureCard
            key={`${pair.theater}-${pair.movie1.start}-${pair.movie2.start}-${index}`}
            pair={pair}
          />
        ))}
      </div>
    </div>
  );
}
